import { createContext, useContext } from "react";
import { Breakpoint } from "../../config/setColumnSpan";

/**
 * The breakpoint the form is drawn at, when it is not the browser's to decide.
 *
 * On the real page the layout answers to the viewport: `col-md-*`, `d-md-flex` and the
 * like are media queries. The designer's tablet and mobile previews are narrow boxes in a
 * wide window, so those queries would answer with the desktop layout. Rather than giving
 * the preview a viewport of its own - an iframe, which drag and drop cannot cross - the
 * canvas says which breakpoint it is showing, and every responsive class resolves to that
 * breakpoint's plain class instead.
 *
 * Null, the default, is the real page: nothing is forced and the media queries decide.
 */
const ForcedBreakpointContext = createContext<Breakpoint | null>(null);

export const ForcedBreakpointProvider = ForcedBreakpointContext.Provider;

/** The breakpoint being forced, or null when the viewport decides. */
export function useForcedBreakpoint(): Breakpoint | null {
  return useContext(ForcedBreakpointContext);
}

export interface ResponsiveClassNames {
  /** What the real page uses, media-query classes and all. */
  viewport: string;
  /** The same layout as plain classes, as it is below `md`. */
  mobile: string;
  /** The same layout as plain classes, as it is from `md` up to `lg`. */
  tablet: string;
  /** From `lg` up. Defaults to `tablet`, as most layouts change only once. */
  desktop?: string;
}

/**
 * The classes for an element that changes with the screen size. Spelled out in full for
 * each case rather than derived: Bootstrap puts the breakpoint infix in a different place
 * for different utilities (`py-md-4`, `justify-content-md-end`, `flex-md-grow-1`), and a
 * helper that guessed would be wrong somewhere quietly.
 */
export function responsiveClassName(
  classes: ResponsiveClassNames,
  forced: Breakpoint | null
): string {
  if (forced === null) {
    return classes.viewport;
  }

  if (forced === "mobile") {
    return classes.mobile;
  }

  return forced === "tablet"
    ? classes.tablet
    : classes.desktop ?? classes.tablet;
}

/** `responsiveClassName` at whatever breakpoint the surrounding canvas is forcing. */
export function useResponsiveClassName(classes: ResponsiveClassNames): string {
  return responsiveClassName(classes, useForcedBreakpoint());
}
